import type { JSX, ReactNode } from 'react';
import { STATUS_TONES, cx } from './styles';
import type { StatusLevel } from './styles';

export interface EmptyStateProps {
  /**
   * 回答"**为什么**这里是空的"。
   *
   * 这是必填项而不是可选装饰：空状态存在的唯一理由就是解释空白。
   * "列表为空"这四个字对用户毫无用处 —— 他需要知道是"没连上"、
   * "还没读取"、"筛选没匹配到"，还是"确实一条都没有"。
   */
  title: ReactNode;
  /** 补充细节：什么时候会有数据、当前筛选条件是什么 */
  description?: ReactNode;
  /** 回答"**下一步做什么**"。没有出口的空状态会把用户逼回浏览器刷新 */
  action?: ReactNode;
  icon?: ReactNode;
  /** 等级：用 error 语义表达"空是因为出错了"，而不是空是因为没有数据 */
  level?: StatusLevel;
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  level = 'neutral',
  compact = false,
  className,
}: EmptyStateProps): JSX.Element {
  const tone = STATUS_TONES[level];

  return (
    <div
      className={cx(
        'flex flex-col items-center justify-center gap-2 text-center',
        compact ? 'px-3 py-4' : 'px-4 py-8',
        className,
      )}
    >
      {icon === undefined ? null : (
        <span aria-hidden="true" className={cx('text-lg', tone.text)}>
          {icon}
        </span>
      )}
      <p className="m-0 text-sm font-medium text-fg">{title}</p>
      {description === undefined ? null : (
        <p className="m-0 max-w-md text-xs text-fg-muted">{description}</p>
      )}
      {action === undefined ? null : <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}
